import { validators } from '@frontend/forms';
import { Button, LoadingSpinnerPanel, Text, Title, YStack } from '@frontend/web-react';
import { CompleteProfileForm } from '@frontend/web/components/SSO';
import { getURLParamData, useAnalytics } from '@frontend/web/hooks';
import { useMcpFlow } from '@frontend/web/hooks/useMcpFlow';
import { useSSOCallback } from '@frontend/web/utils/signup/ssoCallbackStateMachine';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const SSOCallback = () => {
  const { track, trackAdvertising } = useAnalytics();
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const { isMcpFlow } = useMcpFlow();
  const isMCPFlow = searchParams.mcpFlow === true || isMcpFlow;
  const previousStepRef = useRef<string>(undefined);
  const previousSubmittingRef = useRef(false);

  const handleNavigateToMCPComplete = useCallback(() => {
    navigate({ search: { flow: 'mcp' }, to: '/signup/mcp/complete' });
  }, [navigate]);

  const storedParamsRef = useRef(getURLParamData());

  const urlParams = useMemo(
    () => ({
      inviteId: searchParams.inviteId,
      promo: searchParams.promo,
      referrer: searchParams.referrer,
    }),
    [searchParams.inviteId, searchParams.promo, searchParams.referrer],
  );

  const { handleCompleteProfile, isNewSignUp, state } = useSSOCallback({
    isMCPFlow,
    onNavigateToMCPComplete: handleNavigateToMCPComplete,
    urlParams,
  });

  useEffect(() => {
    track({
      data: {
        entryType: 'signup',
        flowVariant: 'v2',
        platform: 'web',
      },
      event: 'ssoCallbackViewed',
      topic: 'auth',
    });
  }, [track]);

  useEffect(() => {
    if (isMCPFlow && state.step === 'missingRequirements') {
      navigate({ search: { flow: 'mcp' }, to: '/signup/mcp/complete' });
    }
  }, [isMCPFlow, navigate, state.step]);

  useEffect(() => {
    const previousStep = previousStepRef.current;

    if (state.step === 'missingRequirements' && previousStep !== 'missingRequirements') {
      track({
        data: {
          entryType: 'signup',
          flowVariant: 'v2',
          isNewSignUp,
          outcome: 'missing_requirements',
          platform: 'web',
        },
        event: 'ssoCallbackOutcome',
        topic: 'auth',
      });
    }

    if (state.step === 'error' && previousStep !== 'error') {
      const isTimeout = state.error === 'Unable to establish connection.';

      if (isTimeout) {
        track({
          data: {
            entryType: 'signup',
            flowVariant: 'v2',
            platform: 'web',
          },
          event: 'ssoCallbackTimeout',
          topic: 'auth',
        });
        track({
          data: {
            entryType: 'signup',
            flowVariant: 'v2',
            isNewSignUp,
            outcome: 'timeout',
            platform: 'web',
          },
          event: 'ssoCallbackOutcome',
          topic: 'auth',
        });
      } else {
        track({
          data: {
            entryType: 'signup',
            errorCategory: 'auth',
            errorCode: 'SSO_CALLBACK_FAILED',
            errorSource: 'ssoCallback',
            flowVariant: 'v2',
            platform: 'web',
            retriable: true,
          },
          event: 'ssoCallbackError',
          topic: 'auth',
        });
        track({
          data: {
            entryType: 'signup',
            errorCategory: 'auth',
            errorCode: 'SSO_CALLBACK_FAILED',
            errorSource: 'ssoCallback',
            flowVariant: 'v2',
            platform: 'web',
            retriable: true,
          },
          event: 'ssoFail',
          topic: 'auth',
        });
        track({
          data: {
            entryType: 'signup',
            flowVariant: 'v2',
            isNewSignUp,
            outcome: 'error',
            platform: 'web',
          },
          event: 'ssoCallbackOutcome',
          topic: 'auth',
        });
      }
    }

    if (
      !isMCPFlow &&
      state.step === 'navigating' &&
      previousStep !== 'navigating' &&
      (previousStep === 'completingAuth' || previousStep === 'missingRequirements')
    ) {
      track({
        data: {
          entryType: 'signup',
          flowVariant: 'v2',
          isNewSignUp,
          outcome: 'authenticated',
          platform: 'web',
        },
        event: 'ssoCallbackOutcome',
        topic: 'auth',
      });
      track({
        data: {
          entryType: 'signup',
          flowVariant: 'v2',
          hasReferrer: Boolean(storedParamsRef.current?.referrer),
          hasUtmParams: Boolean(
            storedParamsRef.current?.utmCampaign ||
              storedParamsRef.current?.utmContent ||
              storedParamsRef.current?.utmMedium ||
              storedParamsRef.current?.utmSource ||
              storedParamsRef.current?.utmTerm,
          ),
          isNewSignUp,
          platform: 'web',
          utmCampaign: storedParamsRef.current?.utmCampaign,
          utmContent: storedParamsRef.current?.utmContent,
          utmMedium: storedParamsRef.current?.utmMedium,
          utmSource: storedParamsRef.current?.utmSource,
          utmTerm: storedParamsRef.current?.utmTerm,
        },
        event: 'ssoSignupCompleted',
        topic: 'auth',
      });
      if (isNewSignUp) {
        trackAdvertising('CompleteRegistration', {
          entryType: 'signup',
          flowVariant: 'v2',
          platform: 'web',
          signupMethod: 'sso',
          utmCampaign: storedParamsRef.current?.utmCampaign,
          utmContent: storedParamsRef.current?.utmContent,
          utmMedium: storedParamsRef.current?.utmMedium,
          utmSource: storedParamsRef.current?.utmSource,
          utmTerm: storedParamsRef.current?.utmTerm,
        });

        const storedParams = getURLParamData();
        if (storedParams?.utmCampaign === 'try-first') {
          track({
            data: {
              entryType: 'frictionless',
              flowVariant: 'frictionless',
              hasTaskDescription: Boolean(storedParams.description),
              method: 'sso_google',
              platform: 'web',
              utmCampaign: storedParams.utmCampaign,
            },
            event: 'experimentSignupCompleted',
            topic: 'auth',
          });
        }
      }
    }

    // eslint-disable-next-line fp/no-mutation
    previousStepRef.current = state.step;
  }, [isMCPFlow, isNewSignUp, state.error, state.step, track, trackAdvertising]);

  useEffect(() => {
    const wasSubmitting = previousSubmittingRef.current;

    if (state.isSubmittingProfile && !wasSubmitting) {
      track({
        data: {
          entryType: 'signup',
          flowVariant: 'v2',
          missingFieldCount: 0,
          platform: 'web',
        },
        event: 'ssoMissingRequirementsSubmitStarted',
        topic: 'auth',
      });
    }

    if (!state.isSubmittingProfile && wasSubmitting) {
      if (state.step === 'navigating') {
        track({
          data: {
            entryType: 'signup',
            flowVariant: 'v2',
            missingFieldCount: 0,
            platform: 'web',
          },
          event: 'ssoMissingRequirementsSubmitSucceeded',
          topic: 'auth',
        });
      }

      if (state.step === 'error') {
        track({
          data: {
            entryType: 'signup',
            errorCategory: 'auth',
            errorCode: 'SSO_COMPLETE_PROFILE_FAILED',
            errorSource: 'ssoCallback',
            flowVariant: 'v2',
            missingFieldCount: 0,
            platform: 'web',
            retriable: true,
          },
          event: 'ssoMissingRequirementsSubmitFailed',
          topic: 'auth',
        });
      }
    }

    // eslint-disable-next-line fp/no-mutation
    previousSubmittingRef.current = state.isSubmittingProfile;
  }, [state.isSubmittingProfile, state.step, track]);

  if (state.step === 'error') {
    return (
      <YStack alignItems="center" className="h-screen" gap="md" justifyContent="center">
        <Text color="danger">Error: {state.error}</Text>
        <Button onClick={() => navigate({ to: '/login' })} variant="primary">
          Try again
        </Button>
      </YStack>
    );
  }

  if (state.step === 'missingRequirements' && !isMCPFlow) {
    return (
      <YStack alignItems="center" className="h-screen" gap="md" justifyContent="center" p="lg">
        <Title type="h5">Complete Your Profile</Title>
        <Text color="secondary">We need a few more details to finish setting up your account.</Text>
        <CompleteProfileForm isLoading={state.isSubmittingProfile} onSubmit={handleCompleteProfile} />
      </YStack>
    );
  }

  return (
    <YStack alignItems="center" className="h-screen" justifyContent="center">
      <LoadingSpinnerPanel />
      <Text className="mt-md">Completing sign in...</Text>
    </YStack>
  );
};

export const Route = createFileRoute('/sso-callback')({
  component: SSOCallback,
  validateSearch: (search) =>
    validators
      .object({
        inviteId: validators.uuid().optional(),
        mcpFlow: validators
          .union([validators.string(), validators.boolean()])
          .optional()
          .transform((val) => val === true || val === 'true'),
        promo: validators.string().optional(),
        referrer: validators.uuid().optional(),
      })
      .parse(search),
});
